import axios from "axios";
import { Logout } from "./commonFunctions";
import {
  getStoredTokens,
  refreshSessionTokens,
} from "./authSession";

const BaseURL = process.env.REACT_APP_BaseURL;

const DEFAULT_HEADERS = {
  "X-Timezone-Id": "Europe/Vilnius",
  "Content-Type": "application/json",
};

const apiClient = axios.create({
  baseURL: BaseURL,
  headers: DEFAULT_HEADERS,
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

apiClient.interceptors.request.use(
  (config) => {
    config.headers = config.headers || {};
    config.headers["X-Timezone-Id"] =
      config.headers["X-Timezone-Id"] || DEFAULT_HEADERS["X-Timezone-Id"];

    // When sending FormData (e.g. image / audio uploads), we MUST let the
    // browser / axios set `Content-Type: multipart/form-data; boundary=...`
    // itself. Our instance default is `application/json`, so we strip it
    // here — otherwise the server receives a multipart body with a JSON
    // Content-Type and returns 500.
    if (typeof FormData !== "undefined" && config.data instanceof FormData) {
      delete config.headers["Content-Type"];
      if (config.headers.common) delete config.headers.common["Content-Type"];
      if (config.headers.post) delete config.headers.post["Content-Type"];
    }

    // Password login (/auth/login) must NOT send X-Version: v3 — that returns 403.
    // OTP / anonymous endpoints still need X-Version.
    const url = config.url || "";
    const isPasswordLogin =
      url === "/auth/login" || url.endsWith("/auth/login");
    if (!isPasswordLogin && !config._skipVersion) {
      config.headers["X-Version"] = "v3";
    } else {
      delete config.headers["X-Version"];
      if (config.headers.common) delete config.headers.common["X-Version"];
    }

    if (config._skipAuth) {
      config.headers["Cache-Control"] = "no-cache";
      return config;
    }

    const tokens = getStoredTokens();
    if (tokens?.accessToken) {
      config.headers["Authorization"] = "Bearer " + tokens.accessToken;
    }
    config.headers["Cache-Control"] = "no-cache";
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error?.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest._skipAuth &&
      window.location.pathname !== "/auth/login"
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers["Authorization"] = "Bearer " + token;
          return apiClient(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const tokens = getStoredTokens();
      if (!tokens?.refreshToken) {
        isRefreshing = false;
        processQueue(error, null);
        Logout();
        return Promise.reject(error);
      }

      try {
        const newTokens = await refreshSessionTokens();

        if (newTokens?.accessToken) {
          processQueue(null, newTokens.accessToken);

          originalRequest.headers["Authorization"] =
            "Bearer " + newTokens.accessToken;
          return apiClient(originalRequest);
        } else {
          processQueue(error, null);
          Logout();
          return Promise.reject(error);
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        Logout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// ── public helpers (same signatures as before) ──────────────────────────

export const ApiGet = async (url, params, isAuth = true, accessToken = null) => {
  const headers = {};
  if (!isAuth) headers._skipAuth = true;
  if (accessToken) headers["Authorization"] = "Bearer " + accessToken;

  const config = { params, headers };
  if (!isAuth) config._skipAuth = true;
  if (accessToken) {
    config.headers = { ...config.headers, Authorization: "Bearer " + accessToken };
    config._skipAuth = true;
  }

  const response = await apiClient.get(url, config);
  return response?.data;
};

export const ApiPost = async (url, body, params, isAuth = true, accessToken = null) => {
  const config = { params };
  if (!isAuth || accessToken) config._skipAuth = true;
  if (accessToken) {
    config.headers = { Authorization: "Bearer " + accessToken };
  }

  const response = await apiClient.post(url, body, config);
  return response?.data;
};

export const ApiPut = async (url, body, params, isAuth = true) => {
  const config = { params };
  if (!isAuth) config._skipAuth = true;

  const response = await apiClient.put(url, body, config);
  return response?.data;
};

export const ApiDelete = async (url, params, isAuth = true) => {
  const config = { params };
  if (!isAuth) config._skipAuth = true;

  const response = await apiClient.delete(url, config);
  return response?.data;
};

export const getHttpOptions = (options = {}) => {
  let headers = {};

  if (options.isAuth !== false) {
    if (options.access_token) {
      headers["Authorization"] = "Bearer " + options.access_token;
    } else {
      const tokens = getStoredTokens();
      if (tokens?.accessToken) {
        headers["Authorization"] = "Bearer " + tokens.accessToken;
      }
    }
    headers["Cache-Control"] = "no-cache";
  }

  if (options.isJsonRequest) {
    headers["Content-Type"] = "application/json";
  }

  if (options.AdditionalParams) {
    headers = { ...headers, ...options.AdditionalParams };
  }

  return { headers };
};

export { apiClient };
